// Three.js creature canvas (v0.8.9 prototype).
//
// Renders a procedural creature into a 128x128 canvas. Idle
// animation: gentle Y rotation + small scale breath. Pauses
// when document.hidden (Pi 4 wall display often sleeps on
// tab-switch, saving the GPU when nobody's looking).
//
// WebGL fallback: if context creation fails, the parent
// component shows the ASCII sprite instead. We report the
// failure via the onFallback callback so the parent can swap
// the engine without unmounting the card.

import { useEffect, useLayoutEffect, useRef } from 'react';
import * as THREE from 'three';
import { buildCreature, paramsFor, type CreatureParams } from './creatures';

interface Props {
  bossName: string;
  status: 'active' | 'vanquished' | 'survived';
  onFallback?: () => void;
}

const CANVAS_SIZE = 128;

export function CreatureCanvas({ bossName, status, onFallback }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const paramsRef = useRef<CreatureParams | null>(null);
  // Track whether the WebGL context is alive. If it dies after
  // the initial mount (e.g. context loss event), we ask the
  // parent to fall back. React's StrictMode double-invokes
  // effects in dev, so this also prevents the same fallback
  // from firing twice.
  const aliveRef = useRef(true);

  useEffect(() => {
    return () => { aliveRef.current = false; };
  }, []);

  useEffect(() => {
    return () => { aliveRef.current = false; };
  }, []);

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Try to create the WebGL renderer. On the Pi 4 wall display
    // most Chrome builds allow WebGL but fall back to a software
    // path; this is fine, just slower. If the context fails to
    // create at all we report it as a fallback.
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas, antialias: false, alpha: true });
    } catch {
      onFallback?.();
      return;
    }
    if (!renderer.getContext()) {
      renderer.dispose();
      onFallback?.();
      return;
    }
    renderer.setPixelRatio(1); // Pi 4 cannot push >1 well; saves GPU.
    renderer.setSize(CANVAS_SIZE, CANVAS_SIZE, false);
    renderer.setClearColor(0x000000, 0); // transparent; the card provides the bg

    // Listen for context loss so the wall display can recover
    // gracefully (Pi sometimes drops WebGL on TV-sleep / wake).
    const canvasEl = renderer.domElement;
    const onContextLost = (e: Event) => {
      e.preventDefault();
      if (aliveRef.current) onFallback?.();
    };
    canvasEl.addEventListener('webglcontextlost', onContextLost, false);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 20);
    camera.position.set(0, 0.3, 3.4);
    camera.lookAt(0, 0, 0);

    // Lights: one ambient + one directional. Both static.
    const ambient = new THREE.AmbientLight(0xffffff, 0.7);
    scene.add(ambient);
    const dir = new THREE.DirectionalLight(0xffffff, 0.7);
    dir.position.set(2, 3, 4);
    scene.add(dir);

    // Build the creature.
    const params = paramsFor(bossName);
    paramsRef.current = params;
    const creature = buildCreature(params);
    creature.position.y = params.yOffset;
    scene.add(creature);

    // Vanquished / survived: desaturate via material adjustments.
    if (status === 'vanquished') {
      creature.traverse((obj) => {
        if ((obj as THREE.Mesh).isMesh) {
          const m = (obj as THREE.Mesh).material as THREE.MeshLambertMaterial;
          m.transparent = true;
          m.opacity = 0.7;
        }
      });
    } else if (status === 'survived') {
      creature.traverse((obj) => {
        if ((obj as THREE.Mesh).isMesh) {
          const m = (obj as THREE.Mesh).material as THREE.MeshLambertMaterial;
          m.transparent = true;
          m.opacity = 0.5;
        }
      });
    }

    // Render loop with idle animation. requestAnimationFrame
    // auto-throttles when tab is hidden, so the Pi isn't
    // spinning cycles when the wall display sleeps.
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let raf = 0;
    let start = performance.now();
    let firstFrame = true;
    const tick = (t: number) => {
      const dt = (t - start) / 1000;
      if (!reduced) {
        // Slow Y rotation (full revolution every ~12s)
        creature.rotation.y = dt * 0.5;
        // Subtle scale breath
        const s = 1 + Math.sin(dt * 1.4) * 0.04;
        creature.scale.set(s, s, s);
      }
      try {
        renderer.render(scene, camera);
        firstFrame = false;
      } catch {
        // render() can throw if the GL context is lost mid-frame
        // (after the contextlost handler was already called, the
        // renderer is unusable). Trigger fallback.
        cancelAnimationFrame(raf);
        if (aliveRef.current) onFallback?.();
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    // Cleanup on unmount
    return () => {
      cancelAnimationFrame(raf);
      canvasEl.removeEventListener('webglcontextlost', onContextLost);
      renderer.dispose();
      creature.traverse((obj) => {
        // Object3D is the base; only Meshes have geometry/material.
        if ((obj as THREE.Mesh).isMesh) {
          const mesh = obj as THREE.Mesh;
          mesh.geometry.dispose();
          const m = mesh.material;
          if (Array.isArray(m)) m.forEach((mm) => mm.dispose());
          else m.dispose();
        }
      });
    };
  }, [bossName, status, onFallback]);

  return (
    <canvas
      ref={canvasRef}
      width={CANVAS_SIZE}
      height={CANVAS_SIZE}
      style={{ width: CANVAS_SIZE, height: CANVAS_SIZE, display: 'block' }}
    />
  );
}
